import clsx from 'clsx';
import uniqid from 'uniqid';

import useTranslation from '@/utils/hooks/translation/useTranslation';

// Types
import type { filtersStateType, setStateFilterType } from '@/components/pages/notifications/types';

import type { CategoryOptionType } from '@/lib/notifications/categories';
// Lib
import { categoryOptions } from '@/lib/notifications/categories';

import Checkbox from '@/components/base/Checkbox';
// Components
import Icon from '@/components/base/Icon';
import type { OptionType } from '@/components/base/Select';
import Select from '@/components/base/Select';

type SelectButtonCategoryProps = {
    error?: string;
    selected?: OptionType[];
    disabled?: boolean;
};

const SelectButtonCategory = ({ selected, disabled, error }: SelectButtonCategoryProps) => {
    const { t } = useTranslation();
    return (
        <Select.Button selected={selected} disabled={disabled} error={error} baseStyle="noBorder">
            <div className="flex w-56 max-w-[14rem] items-center space-x-2 truncate">
                <Icon icon="filter" className="text-icoBlue" />
                <span className="block flex-1 truncate text-icoBlack dark:text-white">
                    {t('notifications:filter_by')}{' '}
                    {selected.map((option, index) => {
                        if (index > 0) {
                            return `, ${t(option.label)}`;
                        }

                        return t(option.label);
                    })}
                </span>
            </div>
        </Select.Button>
    );
};

type SelectOptionCategoryProps = {
    option?: OptionType & {
        icon: string;
    };
    selected?: boolean;
};

const SelectOptionCategory = ({ option, selected }: SelectOptionCategoryProps) => {
    const { t } = useTranslation();
    const isDisabled = option.value === 'all' && selected;
    return (
        <div
            className={clsx('focus inset-y-0 block truncate font-GSemiBold', {
                'text-icoGray-500 dark:text-icoDarkMode-wolf': isDisabled,
                'text-icoBlack dark:text-white': !isDisabled
            })}
            data-testid="select-category-option">
            <label className="pointer-events-none flex cursor-pointer items-center space-x-4">
                <Checkbox
                    readOnly
                    checked={selected}
                    disabled={isDisabled}
                    data-testid={
                        isDisabled
                            ? 'checkbox-disabled'
                            : selected
                              ? 'checkbox-selected'
                              : 'checkbox'
                    }
                />
                <span className="flex items-center space-x-2">
                    {option?.icon ? (
                        <Icon
                            icon={option.icon}
                            className="text-base text-icoBlue"
                            data-testid="option-icon"
                        />
                    ) : null}
                    <span title={option?.label}>{t(option?.label)}</span>
                </span>
            </label>
        </div>
    );
};

type SelectCategoryProps = {
    filters: filtersStateType;
    setFilters: setStateFilterType;
};

const SelectCategory = ({ filters, setFilters }: SelectCategoryProps) => {
    const defaultOption = [categoryOptions[0]];

    const handleSelectOption = (options: CategoryOptionType[]) => {
        let categories = [];

        if (options.length) {
            const lastOption = options[options.length - 1];
            if (lastOption.value !== 'all') {
                categories = options
                    .filter((option) => option.value !== 'all')
                    .map((option) => option.value);
            }
        }

        setFilters((prevState) => ({
            ...prevState,
            categories,
            cacheId: uniqid()
        }));
    };

    return (
        <Select
            options={categoryOptions}
            selected={
                filters.categories.length
                    ? categoryOptions.filter((option) => filters.categories.includes(option.value))
                    : defaultOption
            }
            isMultiple={true}
            setSelected={handleSelectOption}
            button={<SelectButtonCategory />}>
            <SelectOptionCategory />
        </Select>
    );
};

export default SelectCategory;
